import { Role } from '../types/auth.type';

/** Same normalization as chat messages — POST returns Z suffix, GET often omits it. */
export const normalizeServerDateTime = (dateString: string): string =>
  dateString?.endsWith('Z') ? dateString.slice(0, -1) : dateString;

/** Chat-compatible adjustment when server UTC is stored without a Z suffix. */
export const toChatLocalDate = (dateString: string): Date => {
  const date = new Date(normalizeServerDateTime(dateString));
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
};

export const formatChatTimestamp = (dateString: string): string => {
  const localDate = toChatLocalDate(dateString);
  const hours = localDate.getHours();
  const minutes = localDate.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes} ${ampm}`;
};

/** Date + time using the same rules as chat message timestamps. */
export const formatChatDateTime = (dateString: string): string => {
  const localDate = toChatLocalDate(dateString);
  const day = localDate.getDate().toString().padStart(2, '0');
  const month = (localDate.getMonth() + 1).toString().padStart(2, '0');
  const year = localDate.getFullYear();
  return `${day}/${month}/${year} ${formatChatTimestamp(dateString)}`;
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
}; 

export const formatDateOnly = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Arabic "since X days ago" phrasing:
 * 1 → منذ يوم | 2 → منذ يومين | 3–10 → منذ N أيام | 11+ → منذ N يوم
 */
export function formatSinceDaysAgo(count: number, language: string): string {
  const n = Math.abs(Math.floor(count));
  if (language.startsWith('ar')) {
    if (n === 1) return 'منذ يوم';
    if (n === 2) return 'منذ يومين';
    if (n >= 3 && n <= 10) return `منذ ${n} أيام`;
    return `منذ ${n} يوم`;
  }
  if (n === 1) return '1 day ago';
  return `${n} days ago`;
}

/** Numbers always in Western digits (0-9), regardless of UI language */
export const formatNumberEn = (
  value: number | string | null | undefined,
  options?: Intl.NumberFormatOptions,
): string => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('en-US', options);
};

/** Dates with Western digits in the numeric parts */
export const formatDateEn = (
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', options);
};

/** Format a Date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString). */
export const formatLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatRole = (orgId: string, role: string) => `${orgId}.${role}`;

export const getRoleFromPermission = (permission: string): Role => {
  const [, role] = permission.split('.');
  return role as Role;
};

export const displayRole = (role: Role, t: (key: string) => string): string => {
  switch (role) {
    case Role.ADMIN:
      return t('roles.admin');
    case Role.CUSTOMER_SUCCESS:
      return t('roles.customerSuccess');
    case Role.SECTOR_OPERATIONS_MANAGER:
      return t('roles.sectorOperationsManager');
    case Role.ACCOUNTANT:
      return t('roles.accountant');
    case Role.CLEARING_OFFICER:
      return t('roles.clearingOfficer');
    case Role.SALES:
      return t('roles.sales');
    case Role.ORGANIZATION:
      return t('roles.organization');
    case Role.ORGANIZATION_USER:
      return t('roles.organizationUser');
    case Role.AUDITOR:
      return t('roles.auditor');
    default:
      return t('roles.unknownRole');
  }
};

export const isAccountantLike = (role?: Role): boolean =>
  role === Role.ACCOUNTANT
  || role === Role.CLEARING_OFFICER
  || role === Role.SECTOR_OPERATIONS_MANAGER;

/**
 * Validates if a file path is a valid URL or relative path
 * @param filePath - The file path to validate
 * @returns boolean indicating if the path is valid
 */
export const isValidFilePath = (filePath: string): boolean => {
  if (!filePath || typeof filePath !== 'string' || filePath.trim() === '') {
    return false;
  }

  const trimmedPath = filePath.trim();
  return trimmedPath.startsWith('http://') || 
         trimmedPath.startsWith('https://') || 
         trimmedPath.startsWith('/');
};

/**
 * Formats phone number to the required format: 966XXXXXXXXX
 * Removes all non-digit characters, handles +966 and 0 prefixes
 * @param phone - The phone number to format
 * @returns formatted phone number starting with 966
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  let cleaned = phone.replace(/\D/g, '');
  
  // If starts with 0, remove it and add 966
  if (cleaned.startsWith('0')) {
    cleaned = '966' + cleaned.substring(1);
  }
  
  // If doesn't start with 966, add it
  if (!cleaned.startsWith('966')) {
    cleaned = '966' + cleaned;
  }
  
  return cleaned;
};

/**
 * Validates if a phone number is in the correct format
 * @param phone - The phone number to validate
 * @returns boolean indicating if the phone number is valid
 */
export const isValidSaudiPhone = (phone: string): boolean => {
  const formatted = formatPhoneNumber(phone);
  // Saudi phone numbers: 966 + 9 digits (5XXXXXXXX format)
  return /^966[5]\d{8}$/.test(formatted);
};

/**
 * Displays phone number in a user-friendly format
 * Example: 966544825445 -> +966 54 482 5445
 * @param phone - The phone number to display
 * @returns formatted phone number for display
 */
export const displayPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  const formatted = formatPhoneNumber(phone);
  
  // Format: +966 54 482 5445
  if (formatted.startsWith('966') && formatted.length === 12) {
    return `+${formatted.substring(0, 3)} ${formatted.substring(3, 5)} ${formatted.substring(5, 8)} ${formatted.substring(8)}`;
  }
  
  return phone;
};

type ApiValidationErrorItem = {
  msg?: string;
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as {
    message?: string;
    response?: { data?: { detail?: unknown; message?: unknown } };
  };

  const detail = err?.response?.data?.detail ?? err?.response?.data?.message;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as ApiValidationErrorItem).msg);
        }
        return null;
      })
      .filter((msg): msg is string => Boolean(msg));

    if (messages.length > 0) {
      return messages.join(' · ');
    }
  }

  if (detail && typeof detail === 'object' && 'msg' in detail) {
    return String((detail as ApiValidationErrorItem).msg);
  }

  if (err?.message && !err.message.startsWith('Request failed with status code')) {
    return err.message;
  }

  return fallback;
};

